import React from 'react';
import { useSelector } from 'react-redux';
import {
  selectActivationTrackerPhase,
  type ActivationTrackerPhase,
} from '../../../redux/ownerActivationSlice';

/**
 * Phase-driven boundary between the activation tracker and normal owner
 * Quick Actions.
 *
 * Both Home implementations (native and web) render this single slot where
 * Quick Actions normally live:
 * - `inactive` / `completed_acknowledged`: normal Quick Actions render;
 * - `active` / `completed_unacknowledged`: the tracker replaces them.
 *
 * The tracker itself (with task wiring, celebration, and analytics) is
 * injected so this boundary owns no story-specific behavior.
 */

export type OwnerGuidanceSlotProps = {
  /** Normal Quick Actions section (unchanged existing component). */
  renderQuickActions: () => React.ReactNode;
  /** Connected activation tracker for active/unacknowledged phases. */
  renderTracker: (phase: ActivationTrackerPhase) => React.ReactNode;
};

export function shouldRenderActivationTracker(
  phase: ActivationTrackerPhase,
): boolean {
  return phase === 'active' || phase === 'completed_unacknowledged';
}

const OwnerGuidanceSlot: React.FC<OwnerGuidanceSlotProps> = ({
  renderQuickActions,
  renderTracker,
}) => {
  const phase = useSelector(selectActivationTrackerPhase);
  return (
    <>
      {shouldRenderActivationTracker(phase)
        ? renderTracker(phase)
        : renderQuickActions()}
    </>
  );
};

export default OwnerGuidanceSlot;
